"use client";

import { memo, useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { FundAgentSlide } from "./FundAgentSlide";
import { SubAgentSlide } from "./SubAgentSlide";

interface FundAgentData {
  dataframes: Record<string, {
    data: Array<Record<string, any>>;
    schema: {
      columns: string[];
      types: Record<string, string>;
      row_count: number;
    };
    sources: Array<{ name: string; url: string }>;
    reasoning: string;
  }>;
  sub_notebook_id: string;
  query: string;
}

interface SubAgentData {
  dataframes: Record<string, {
    data: Array<Record<string, any>>;
    schema: {
      columns: string[];
      types: Record<string, string>;
      row_count: number;
    };
    sources?: Array<{ name: string; url: string }>;
    reasoning: string;
  }>;
  charts?: Array<{ plotly_json: any; title?: string }>;
  summary?: string;
  sub_notebook_id: string;
  task: string;
  functions: string[];
}

type AgentResult =
  | { type: "fund_agent"; data: FundAgentData }
  | { type: "sub_agent"; data: SubAgentData };

interface AgentResultsCarouselProps {
  results: AgentResult[];
}


function AgentResultsCarouselComponent({ results }: AgentResultsCarouselProps) {
  const [currentSlide, setCurrentSlide] = useState(0);

  if (!results || results.length === 0) return null;

  const goToPrev = () => setCurrentSlide((prev) => (prev === 0 ? results.length - 1 : prev - 1));
  const goToNext = () => setCurrentSlide((prev) => (prev === results.length - 1 ? 0 : prev + 1));
  const goToSlide = (index: number) => setCurrentSlide(index);

  const current = results[currentSlide];
  const displayLabel =
    current.type === "fund_agent"
      ? current.data.query || ""
      : current.data.task || "";
  const truncatedLabel =
    displayLabel.length > 60
      ? displayLabel.slice(0, 60) + "…"
      : displayLabel;

  const renderSlide = (result: AgentResult) => {
    if (result.type === "fund_agent") {
      return (
        <FundAgentSlide
          query={result.data.query}
          dataframes={result.data.dataframes}
        />
      );
    }
    return (
      <SubAgentSlide
        task={result.data.task}
        summary={result.data.summary}
        dataframes={result.data.dataframes}
        charts={result.data.charts}
        functions={result.data.functions}
      />
    );
  };

  const hasMultiple = results.length > 1;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.15 }}
      className="w-full"
    >
      {/* Header row: badge + title left, nav right */}
      <div className="flex items-center justify-between gap-2 mb-3">
        <p
          className="text-[13px] font-medium text-foreground/70 truncate min-w-0 flex-1"
          title={displayLabel}
        >
          {truncatedLabel}
        </p>
        {hasMultiple && (
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              type="button"
              onClick={goToPrev}
              className="w-6 h-6 flex items-center justify-center rounded-full text-muted-foreground/50 hover:text-foreground hover:bg-muted/30 transition-colors"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            <div className="flex items-center gap-1 px-1">
              {results.map((r, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => goToSlide(index)}
                  className={`rounded-full transition-all duration-200 ${
                    index === currentSlide
                      ? "w-5 h-1 bg-primary"
                      : "w-1.5 h-1.5 bg-muted-foreground/20 hover:bg-muted-foreground/40"
                  }`}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={goToNext}
              className="w-6 h-6 flex items-center justify-center rounded-full text-muted-foreground/50 hover:text-foreground hover:bg-muted/30 transition-colors"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
            <span className="text-[10px] text-muted-foreground/40 tabular-nums ml-0.5">
              {currentSlide + 1}/{results.length}
            </span>
          </div>
        )}
      </div>

      {/* Slide Container — active slide sets natural height, inactive slides are absolute/invisible */}
      <div className="relative w-full">
        {results.map((result, index) => (
          <div
            key={index}
            className="w-full"
            style={{
              position: index === currentSlide ? "relative" : "absolute",
              top: 0,
              left: 0,
              opacity: index === currentSlide ? 1 : 0,
              pointerEvents: index === currentSlide ? "auto" : "none",
              transition: "opacity 200ms ease-out",
              visibility: index === currentSlide ? "visible" : "hidden",
            }}
          >
            {renderSlide(result)}
          </div>
        ))}
      </div>
    </motion.div>
  );
}

export const AgentResultsCarousel = memo(AgentResultsCarouselComponent);
